import React, {useRef, useState} from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import styles from './ScreenshotCarousel.module.scss';

interface IScreenshotCarouselProps {
    images: string[],
}

const SWIPE_THRESHOLD = 40;

const Chevron: React.FC = () => (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8 2L2 8L8 14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ScreenshotCarousel: React.FC<IScreenshotCarouselProps> = ({images}) => {
    const [index, setIndex] = useState(0);
    const touchStartX = useRef<number | null>(null);

    const count = images.length;

    const goTo = (next: number) => {
        if (count === 0) return;
        setIndex((next + count) % count);
    };

    const onTouchStart = (event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    };

    const onTouchEnd = (event: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > SWIPE_THRESHOLD) {
            goTo(index + (delta < 0 ? 1 : -1));
        }
        touchStartX.current = null;
    };

    if (count === 0) return null;

    return (
        <div className={styles.carousel}>
            <div
                className={styles.viewport}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
            >
                {images.map((src, i) => (
                    <img
                        key={src}
                        className={cn(styles.image, i === index && styles.imageActive)}
                        src={src}
                        alt={`Report preview ${i + 1}`}
                        draggable={false}
                    />
                ))}
            </div>

            {count > 1 && (
                <div className={styles.controls}>
                    <button
                        type="button"
                        className={cn(styles.nav, styles.prev)}
                        onClick={() => goTo(index - 1)}
                        aria-label={t`Предыдущий скриншот`}
                    >
                        <Chevron/>
                    </button>
                    <div className={styles.dots}>
                        {images.map((src, i) => (
                            <button
                                type="button"
                                key={src}
                                className={cn(styles.dot, i === index && styles.dotActive)}
                                onClick={() => goTo(i)}
                                aria-label={t`Показать скриншот ${i + 1}`}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        className={cn(styles.nav, styles.next)}
                        onClick={() => goTo(index + 1)}
                        aria-label={t`Следующий скриншот`}
                    >
                        <Chevron/>
                    </button>
                </div>
            )}
        </div>
    );
};

export default ScreenshotCarousel;
